import { Suspense, useRef, useEffect, useMemo } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";

const PATCH_TEX = "/assets/lifewave_patch.png";

// Deterministic pseudo-random
function rand(seed) {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

/**
 * One looping patch: emerges from the X39 box origin, flies outward & up
 * along its own arc, fades out, then respawns. Each patch has its own cycle
 * length & phase so the stream feels organic.
 */
function Patch({ index, total, cycle, texture }) {
  const groupRef = useRef();
  const planeRef = useRef();
  const haloRef = useRef();

  const config = useMemo(() => {
    const r1 = rand(index + 1);
    const r2 = rand(index + 2.7);
    const r3 = rand(index + 5.3);
    const r4 = rand(index + 8.9);
    // Emerge from where the box sits in the video (center, slightly below middle)
    const origin = [0, -1.0, 0];
    // Target: spread outward in a fountain pattern
    const angle = (index / total) * Math.PI * 2 + r1 * 0.4;
    const distance = 3.2 + r2 * 2.6;
    const target = [
      origin[0] + Math.cos(angle) * distance,
      origin[1] + 1.5 + r3 * 3.5, // mostly upward
      origin[2] + Math.sin(angle) * 1.6 + (r4 - 0.5) * 2,
    ];
    const phase = index / total; // stagger start
    const cycleLen = cycle * (0.9 + r2 * 0.3); // 90-120% of base cycle
    const spinSign = r4 > 0.5 ? 1 : -1;
    const baseScale = 0.45 + r3 * 0.25;
    const tiltX = (r4 - 0.5) * 0.5;
    const tiltZ = (r1 - 0.5) * 0.5;
    return { origin, target, phase, cycleLen, spinSign, baseScale, tiltX, tiltZ };
  }, [index, total, cycle]);

  useFrame(({ clock }) => {
    if (!groupRef.current) return;
    const t = clock.elapsedTime;
    // Normalized progress through this patch's lifecycle (0..1, then wraps)
    let raw = ((t / config.cycleLen) + config.phase) % 1;
    const p = raw; // 0 = born at origin, 1 = end of cycle
    // Easing: quick emerge + gentle fly-out, then fade out tail
    const flyEase = p < 0.55 ? 1 - Math.pow(1 - p / 0.55, 3) : 1;
    // Position
    const pos = config.origin.map((o, i) =>
      o + (config.target[i] - o) * flyEase
    );
    groupRef.current.position.set(pos[0], pos[1], pos[2]);
    // Rotation: tumble during flight
    groupRef.current.rotation.x = config.tiltX + t * 0.4 * config.spinSign + index;
    groupRef.current.rotation.y = t * 0.6 + index * 0.7;
    groupRef.current.rotation.z = config.tiltZ + Math.sin(t * 0.45 + index) * 0.15;
    // Scale: tiny at origin, grows to full while flying, then shrinks at tail
    let s;
    if (p < 0.15) s = (p / 0.15) * config.baseScale; // grow
    else if (p < 0.75) s = config.baseScale;
    else s = config.baseScale * (1 - (p - 0.75) / 0.25) * 0.95 + config.baseScale * 0.05; // shrink
    groupRef.current.scale.setScalar(Math.max(0.001, s));
    // Opacity: fade in at start, fade out at tail
    let alpha;
    if (p < 0.12) alpha = p / 0.12;
    else if (p < 0.7) alpha = 1;
    else alpha = Math.max(0, 1 - (p - 0.7) / 0.3);
    if (planeRef.current?.material) planeRef.current.material.opacity = alpha;
    if (haloRef.current?.material) {
      haloRef.current.material.opacity =
        alpha * (0.5 + Math.sin(t * 1.5 + index) * 0.15);
    }
  });

  return (
    <group ref={groupRef}>
      <mesh ref={haloRef} position={[0, 0, -0.06]}>
        <circleGeometry args={[1.05, 48]} />
        <meshBasicMaterial color="#00F0FF" transparent opacity={0.4} depthWrite={false} />
      </mesh>
      <mesh ref={planeRef}>
        <planeGeometry args={[1.6, 1.6]} />
        <meshBasicMaterial
          map={texture}
          transparent
          side={THREE.DoubleSide}
          toneMapped={false}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}

function Scene({ count, cycle }) {
  const texture = useLoader(THREE.TextureLoader, PATCH_TEX);
  useMemo(() => {
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 8;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
  }, [texture]);

  return (
    <>
      <ambientLight intensity={1} />
      {Array.from({ length: count }).map((_, i) => (
        <Patch key={i} index={i} total={count} cycle={cycle} texture={texture} />
      ))}
    </>
  );
}

/**
 * Continuously looping global patch stream — patches emerge from the X39 box
 * in the background video, fly outward, fade out, respawn. Plays forever
 * regardless of scroll position.
 */
export default function GlobalPatchStream({ count = 5, cycle = 7 }) {
  return (
    <div
      data-testid="global-patch-stream"
      className="pointer-events-none fixed inset-0 z-[20]"
      aria-hidden
    >
      <Canvas
        camera={{ position: [0, 0, 8], fov: 50 }}
        dpr={[1, 1.5]}
        style={{ pointerEvents: "none" }}
      >
        <Suspense fallback={null}>
          <Scene count={count} cycle={cycle} />
        </Suspense>
      </Canvas>
    </div>
  );
}
